import { api, API_URL, authHeaders, getToken, resolvePath } from '../config/api';

// ---- Auth ----
export async function login(email, password) {
  const res = await api('/auth', { method: 'POST', body: { email, password } });
  return { token: res.token, user: res.user };
}

export async function fetchMe() {
  // API responds `{ message: { user, validationRequired } }`; api() unwraps the
  // `message`, so we take `.user` (which carries `stores` — the user's brands).
  const res = await api('/user/me');
  return res?.user ?? res;
}

/// The session plus whatever else the boot response carries. Embedded only:
/// /shopify/admin/session adds `returnTo` when this Shopify session began with
/// "add a brand" in the portal, and the store has just been attached to that
/// account. The API hands it out exactly once, so read it here and act on it.
export async function fetchSession() {
  const res = await api('/user/me');
  return {
    user: res?.user ?? res,
    returnTo: res?.returnTo ?? null,
    portalUrl: res?.portalUrl ?? null,
    claimUrl: res?.claimUrl ?? null,
    linked: res?.linked ?? false,
  };
}

// ---- Brand fetch ----
export async function fetchBrand(brandId, { fetchLiveMarkets = false } = {}) {
  const q = fetchLiveMarkets ? '' : '?fetch_live_markets=false';
  return api(`/brands/${brandId}${q}`);
}

// ---- Brand PROFILE update (PUT /brands/:id) ----
// IMPORTANT: profile fields use CAMELCASE keys (storeName, companyName, …) —
// the backend's updateBrand maps `updates.storeName` → store_name etc.
// (`tagline` is the same on both sides). IBAN and visibility instead use
// snake_case (iban / is_active). Optional logo / cover → multipart.
//
// Multipart field names — the backend's PUT /brands/:id (and the embedded
// PUT /shopify/admin/brand) use multer `upload.fields([files, logo, cover])`:
//   files  the logo (legacy name; the mobile app and every older API build
//          accept it, so the logo keeps working during a staggered deploy)
//   cover  the cover image → storage/brands/<id>/cover.jpg
// Any other file field is a MulterError "Unexpected field". Both files are
// overwritten in place server-side; the API answers with cache-busted
// `logo` / `cover_image` URLs (?v=<mtime>) so previews can swap to the stored
// files right away.
export const BRAND_LOGO_FIELD = 'files';
export const BRAND_COVER_FIELD = 'cover';

export function buildBrandProfileForm(fields, logoFile, coverFile) {
  const form = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    form.append(k, Array.isArray(v) ? JSON.stringify(v) : v);
  });
  if (logoFile) form.append(BRAND_LOGO_FIELD, logoFile);
  if (coverFile) form.append(BRAND_COVER_FIELD, coverFile);
  return form;
}

export async function updateBrandProfile(brandId, fields, logoFile, coverFile) {
  if (logoFile || coverFile) {
    const form = buildBrandProfileForm(fields, logoFile, coverFile);
    // This branch bypasses api(), so it has to do the credential and path
    // resolution itself — in the Shopify iframe both differ.
    const res = await fetch(`${API_URL}${resolvePath(`/brands/${brandId}`)}`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: form,
    });
    return unwrapWrite(res);
  }
  return api(`/brands/${brandId}`, { method: 'PUT', body: fields });
}

// IBAN + visibility both hit PUT /brands/:id with a SNAKE_CASE body.
export async function updateBrandIban(brandId, iban) {
  return api(`/brands/${brandId}`, {
    method: 'PUT',
    body: { iban: iban && iban.trim() ? iban.trim() : null },
  });
}
export async function toggleBrandVisibility(brandId, isActive) {
  return api(`/brands/${brandId}`, {
    method: 'PUT',
    body: { is_active: isActive },
  });
}

export async function deleteBrand(brandId) {
  return api(`/brands/${brandId}`, { method: 'DELETE' });
}

async function unwrapWrite(res) {
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data?.data ?? data?.message ?? data;
}

// ---- Products + sync ----
/// `includeNonClothing` is not optional here. getBrandProducts defaults to
/// `is_clothing: true`, which is right for the shopper-facing surfaces but
/// wrong for the merchant's own catalogue: anything the AI classifier did not
/// call clothing simply vanished, so a store could sync 17 products and see 1.
/// The embedded product list already returns everything; this keeps the portal
/// showing the same catalogue.
export async function fetchBrandProducts(brandId) {
  return api(`/brands/${brandId}/products?includeNonClothing=true`);
}
/// Issue #615 — live counts for the Products tab's progress bar, polled while a
/// sync runs. Answers `{ status, total, processed, startedAt, finishedAt }`;
/// `total` is null when the run has started but has not yet heard back from
/// Shopify with a catalogue size, which the bar shows as indeterminate.
export async function fetchSyncProgress(brandId) {
  return api(`/brands/${brandId}/sync-progress`);
}
export async function syncProducts(brandId) {
  return api(`/brands/${brandId}/sync-products`, { method: 'POST', body: {} });
}
/// documents#188 — "label this product again". Queues the row (ai_labeling
/// PENDING) and asks the labelling job to run now. Resolves with the 202 body,
/// `{ status: 'PENDING', product_id, ai_labeling, run }` (api() unwraps the
/// `message` envelope). Throws with `.status` 404 (product_not_found) or 409
/// (product_not_labelable: deleted, or no Shopify data to label from).
/// Embedded: mapPath rewrites this to /shopify/admin/products/:pid/relabel.
export async function relabelProduct(brandId, productId) {
  return api(`/brands/${brandId}/products/${productId}/relabel`, { method: 'POST', body: {} });
}

export async function fetchLabelDisputes(brandId) {
  const res = await api(`/brands/${brandId}/label-disputes`);
  return Array.isArray(res?.disputes) ? res.disputes : [];
}

export async function createLabelDispute(brandId, productId, message) {
  const text = typeof message === 'string' ? message.trim() : '';
  return api(`/brands/${brandId}/products/${productId}/label-disputes`, {
    method: 'POST',
    body: text ? { message: text } : {},
  });
}

export function apiErrorCode(err) {
  const code = err?.data?.message ?? err?.data?.error ?? err?.message;
  return typeof code === 'string' ? code : null;
}

export async function testShopifyConnection(brandId) {
  return api(`/brands/${brandId}/test-shopify-connection`);
}

// ---- Members ----
export async function fetchBrandMembers(brandId) {
  // Response is the members array (under `message`).
  return api(`/brands/${brandId}/members`);
}
export async function addBrandMember(brandId, userId, permissions, role) {
  return api(`/brands/${brandId}/members`, {
    method: 'POST',
    body: { userId, permissions, ...(role ? { role } : {}) },
  });
}
export async function updateBrandMember(brandId, userId, permissions, role) {
  return api(`/brands/${brandId}/members/${userId}`, {
    method: 'PUT',
    body: { permissions, ...(role ? { role } : {}) },
  });
}
export async function removeBrandMember(brandId, userId) {
  return api(`/brands/${brandId}/members/${userId}`, { method: 'DELETE' });
}

// User search for adding members — hits /users/search?keyword=… (username-based,
// like the mobile app). Returns only `type: 'user'` rows (the endpoint also
// mixes in brands). Each row has { id, username, avatarUrl, first_name, ... }.
export async function searchUsers(keyword) {
  const q = new URLSearchParams({ keyword, limit: '20' }).toString();
  const res = await api(`/users/search?${q}`);
  const arr = Array.isArray(res) ? res : res?.data ?? [];
  return arr.filter((r) => (r.type ? r.type === 'user' : true));
}

// ---- Drops ----
// GET /v3/drops/brand/:brandId?all=true — all drops for the brand (no market
// filtering). Response is the drops array (under `message.data` → unwrapped).
export async function fetchBrandDrops(brandId) {
  // Envelope is `{ message: { success, data:[...] } }`; api() unwraps `message`,
  // leaving `{ success, data }` — take the drops array.
  const res = await api(`/drops/brand/${brandId}?all=true`);
  return res?.data ?? res ?? [];
}

export async function createDrops(brandId, { productIds, dropDate, exclusive }) {
  const res = await api('/drops/batch', {
    method: 'POST',
    body: { brand_id: brandId, product_ids: productIds, drop_date: dropDate, exclusive },
  });
  return res?.data ?? res;
}

export async function cancelDrop(dropId) {
  const res = await api(`/drops/${dropId}`, { method: 'PUT', body: { status: 'cancelled' } });
  return res?.data ?? res;
}

// ---- Discounts (#704) ----
// Brand discount codes, kept in sync with the brand's Shopify store. The list
// call can sync first (`sync: true`); every write goes to Shopify and back.
export async function fetchBrandDiscounts(brandId, { sync = false } = {}) {
  const res = await api(`/brands/${brandId}/discounts${sync ? '?sync=true' : ''}`);
  return { discounts: res?.discounts ?? [], currency: res?.currency ?? null, syncError: res?.sync_error ?? null };
}

export async function syncBrandDiscounts(brandId) {
  const res = await api(`/brands/${brandId}/discounts/sync`, { method: 'POST' });
  return res?.discounts ?? [];
}

export async function createBrandDiscount(brandId, input) {
  return api(`/brands/${brandId}/discounts`, { method: 'POST', body: input });
}

export async function setBrandDiscountPaused(brandId, discountId, paused) {
  return api(`/brands/${brandId}/discounts/${discountId}`, { method: 'PATCH', body: { paused } });
}

export async function deleteBrandDiscount(brandId, discountId) {
  return api(`/brands/${brandId}/discounts/${discountId}`, { method: 'DELETE' });
}

// ---- Challenge calendar ----
// Outfino's daily community challenges, read-only (D11): every day of
// [from, to] (Europe/Budapest, YYYY-MM-DD) with every non-cancelled challenge,
// every brand's rewards (brand, type, size, quantity) and this brand's own
// items and partner days marked (is_own). Drafts and not-yet-final days come
// with may_change: true.
export async function fetchChallengeCalendar(brandId, { from, to }) {
  const query = new URLSearchParams({ from, to }).toString();
  return api(`/brands/${brandId}/challenge-calendar?${query}`);
}

// ---- Prize Pool (D7–D9) ----
// The brand's own offers for the community challenges, with the month's stats.
// Reading needs viewProducts; creating, editing and withdrawing need
// manageCampaigns. Edits and withdrawals send the offer's `version`.
export async function fetchPrizePool(brandId, month) {
  return api(`/brands/${brandId}/prize-pool${month ? `?month=${encodeURIComponent(month)}` : ''}`);
}

export async function createPrizePoolOffer(brandId, offer) {
  return api(`/brands/${brandId}/prize-pool/offers`, { method: 'POST', body: offer });
}

export async function updatePrizePoolOffer(brandId, offerId, version, changes) {
  return api(`/brands/${brandId}/prize-pool/offers/${offerId}`, { method: 'PATCH', body: { ...changes, version } });
}

export async function withdrawPrizePoolOffer(brandId, offerId, version) {
  return api(`/brands/${brandId}/prize-pool/offers/${offerId}/withdraw`, { method: 'POST', body: { version } });
}

// ---- Creator campaigns ----
// Brand side of the creator collaboration. All endpoints require a brand member
// with the `manageCampaigns` permission (owner auto-has it). The api() helper
// unwraps the `{ success, data }` / `{ message }` envelope, so list functions
// return the array/record directly.
export async function fetchBrandCampaigns(brandId) {
  const res = await api(`/brands/${brandId}/campaigns`);
  return Array.isArray(res) ? res : res?.data ?? [];
}
export async function createBrandCampaign(brandId, payload) {
  return api(`/brands/${brandId}/campaigns`, { method: 'POST', body: payload });
}
export async function fetchBrandCampaign(brandId, id) {
  return api(`/brands/${brandId}/campaigns/${id}`);
}
export async function selectCampaignApplicant(brandId, id, userId) {
  return api(`/brands/${brandId}/campaigns/${id}/applicants/${userId}/select`, {
    method: 'POST',
    body: {},
  });
}
export async function rejectCampaignApplicant(brandId, id, userId) {
  return api(`/brands/${brandId}/campaigns/${id}/applicants/${userId}/reject`, {
    method: 'POST',
    body: {},
  });
}
// Submissions review (wired lazily — no UI in the current CampaignsTab yet).
export async function fetchCampaignSubmissions(brandId, id) {
  const res = await api(`/brands/${brandId}/campaigns/${id}/submissions`);
  return Array.isArray(res) ? res : res?.data ?? [];
}
export async function approveCampaignSubmission(brandId, id, submissionId) {
  return api(
    `/brands/${brandId}/campaigns/${id}/submissions/${submissionId}/approve`,
    { method: 'POST', body: {} }
  );
}
export async function rejectCampaignSubmission(brandId, id, submissionId, reviewNote) {
  return api(
    `/brands/${brandId}/campaigns/${id}/submissions/${submissionId}/reject`,
    { method: 'POST', body: reviewNote ? { reviewNote } : {} }
  );
}

// ---- Stats ----
export async function fetchStatsOverview(brandId) {
  return api(`/brands/${brandId}/stats/overview`);
}
export async function fetchRevenue(brandId, period = 'month') {
  return api(`/brands/${brandId}/stats/revenue?period=${period}`);
}
export async function fetchBrandOrders(brandId, limit = 50, offset = 0) {
  return api(`/brands/${brandId}/orders?limit=${limit}&offset=${offset}`);
}
export async function fetchPayouts(brandId, limit = 10) {
  return api(`/brands/${brandId}/stats/payouts?limit=${limit}`);
}
export async function requestPayout(brandId, amount, paymentMethod = 'bank_transfer', notes) {
  return api(`/brands/${brandId}/stats/payouts/request`, {
    method: 'POST',
    body: { amount, payment_method: paymentMethod, notes: notes || null },
  });
}

// ---- Shopify connect ----
// Open /v3/shopify/auth?token=<userToken>&return_to=<here>. With no `shop`, the
// API hands the merchant to Shopify, who asks them to log in and pick the store
// — the picking happens entirely on Shopify's side.
//
// That means the install carries no Outfino identity and there is no shop to
// remember one against. The link is made on the way back instead: the embedded
// app sends the merchant here with a signed connect token, and linkShopifyStore
// below trades it for the claim. `return_to` is only a hint for the case where
// the API already knew the shop.
export function shopifyConnectUrl(shop) {
  const token = getToken();
  const params = new URLSearchParams({ token: token || '' });
  if (shop) params.set('shop', shop);
  if (typeof window !== 'undefined') {
    params.set('return_to', `${window.location.origin}${window.location.pathname}`);
  }
  return `${API_URL}/shopify/auth?${params.toString()}`;
}

/// Trade the connect token the embedded app sent us for ownership of the store.
/// Authorised twice over: this call carries the user's Outfino token, and the
/// connect token proves a Shopify session for that shop asked to be linked.
/// Returns { brandId, shop }.
export async function linkShopifyStore(connectToken) {
  return api('/shopify/link', { method: 'POST', body: { connectToken } });
}

// ---- Shipping (#623 B4 — READ ONLY; Shopify is the only source) ----
//
// The brand's Shopify shipping setup, surfaced in the portal so a merchant can
// see which countries they ship to and, per country, which methods a shopper
// would be offered. Nothing here writes: per the 2026-09-22 decision the
// shipping fee has ONE source (Shopify), so the former brand-owned rate CRUD
// helpers (B5, /shipping/rates/brand) were removed from the portal. Coverage
// and fees are changed in the Shopify Admin and flow back via webhook.
//
// Two endpoints back the tab:
//   • GET /shipping/coverage?brand_id=<id> — the whole picture at once:
//     distinct covered countries (ISO alpha-2), whether a Rest-of-World zone
//     exists, the brand's full method list (NOT country-specific), and the
//     state of the CACHED coverage the shopper filters / checkout gate use:
//     coverage_status ('VALID'|'STALE'|'UNKNOWN'|'ERROR') + coverage_updated_at
//     (ISO or null). Error states come back 200 with an `error` flag
//     ('not_connected' | 'shipping_access_denied'), so read `error` before
//     trusting the rest — the coverage_* fields are present on those too.
//   • POST /shipping/rates — the exact methods for ONE country, resolved by
//     Shopify. Called when a country on the map is clicked.
export async function fetchShippingCoverage(brandId) {
  // A brand-scoped query-string endpoint, not a /brands/:id path — so it needs
  // no embedded path rewrite. api() still attaches the right credential.
  const q = new URLSearchParams({ brand_id: String(brandId) }).toString();
  return api(`/shipping/coverage?${q}`);
}

// The country-specific methods for `countryCode`. `subtotal` is only there to
// give Shopify a cart to price against — the method LIST is what we want, so a
// representative value (default 0) is fine. Returns the array of
// `available_methods` for this brand, or [] when Shopify offered none.
export async function fetchShippingRates(
  brandId,
  countryCode,
  { subtotal = 0, currency = 'EUR', targetCurrency } = {}
) {
  const res = await api('/shipping/rates', {
    method: 'POST',
    body: {
      brands: [{ brand_id: brandId, subtotal, currency }],
      country_code: countryCode,
      ...(targetCurrency ? { target_currency: targetCurrency } : {}),
    },
  });
  // The response is keyed per brand. Shapes seen in the wild: an array of
  // `{ brand_id, available_methods }`, or an object keyed by brand id, or the
  // brand record inlined. Dig out this brand's methods without assuming one.
  const pick = (entry) => (Array.isArray(entry?.available_methods) ? entry.available_methods : null);

  if (Array.isArray(res)) {
    const mine = res.find((b) => String(b?.brand_id) === String(brandId));
    return pick(mine) || pick(res[0]) || [];
  }
  if (res && typeof res === 'object') {
    if (Array.isArray(res.brands)) {
      const mine = res.brands.find((b) => String(b?.brand_id) === String(brandId));
      return pick(mine) || pick(res.brands[0]) || [];
    }
    return pick(res[brandId]) || pick(res) || [];
  }
  return [];
}

// ---- Permission helpers ----
export const BRAND_PERMISSIONS = [
  'viewProducts',
  'editProducts',
  'viewOrders',
  'viewAnalytics',
  'manageMembers',
  'manageCampaigns',
  'editBrand',
  // Számlázás: the card and the billing details (CEO, 2026-11-03). Off for a
  // new member; the owner always has it (like every permission).
  'manageBilling',
];

/// The current user's permission set for a brand, derived from the session
/// `stores[]` entry's `members` list. The API puts exactly one entry there: the
/// signed-in user's OWN membership ({ userId, role, isActive, permissions }),
/// worked out the same way its requireBrandPermission decides — never the
/// brand's other members. Owners get all permissions. Platform admins (passed
/// in) bypass. Returns a Set of permission strings.
///
/// Only a menu: every brand endpoint is still checked server-side.
export function permissionsForUser(brand, userId, isAdmin) {
  if (isAdmin) return new Set(BRAND_PERMISSIONS);
  if (!userId) return new Set();
  const members = brand?.members || [];
  const mine = members.find(
    (m) => (m.user?.id || m.userId || m.user_id) === userId
  );
  if (!mine || mine.isActive === false) return new Set();
  const role = mine.role;
  if (role === 'owner') return new Set(BRAND_PERMISSIONS);
  const perms = mine.permissions;
  if (Array.isArray(perms)) return new Set(perms);
  if (perms && typeof perms === 'object') {
    return new Set(Object.keys(perms).filter((k) => perms[k]));
  }
  return new Set();
}
